import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from './api';
import { useAuth } from '../../context/AuthContext';
import PlanningEngine from './PlanningEngine';

const STATUS_COLOR = { draft:'#9A8A7A', review:'#B8860B', approved:'#2A7A5A', archived:'#A09080' };
const STATUS_ICON  = { draft:'✏️', review:'🔍', approved:'✅', archived:'📦' };

export default function PlanEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canEdit, canApprove, isAdmin } = useAuth();

  const [plan, setPlan]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [comment, setComment]     = useState('');
  const [showPanel, setShowPanel] = useState(false); // right sidebar
  const [versions, setVersions]   = useState([]);

  const load = useCallback(() => {
    api.getPlan(id).then(p => {
      setPlan(p);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ── Save handler passed to PlanningEngine ────────────────────────────────
  const handleSave = useCallback(async (engineData) => {
    setSaveState('saving');
    try {
      await api.savePlan(id, { data: engineData, name: engineData.planName });
      setPlan(p => ({ ...p, data: engineData, name: engineData.planName }));
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch(e) {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  }, [id]);

  async function handleStatusAction(action) {
    if (!window.confirm(`${action} this plan?`)) return;
    try {
      if (action === 'Submit for Review') await api.submitPlan(id);
      if (action === 'Approve')           await api.approvePlan(id);
      if (action === 'Reject to Draft')   await api.rejectPlan(id);
      if (action === 'Archive')           await api.archivePlan(id);
      load();
    } catch(e) { alert(e.message); }
  }

  async function handleComment() {
    if (!comment.trim()) return;
    await api.addComment(id, comment);
    setComment('');
    load();
  }

  async function loadVersions() {
    const v = await api.getPlanVersions(id);
    setVersions(v);
  }

  const isReadOnly = !canEdit ||
    plan?.status === 'approved' && !isAdmin ||
    plan?.status === 'archived';

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:'#9A8A7A' }}>
      Loading plan…
    </div>
  );

  if (!plan) return (
    <div style={{ padding:40, color:'#A02020' }}>Plan not found.</div>
  );

  return (
    <div style={{ display:'flex', height:'100vh', flexDirection:'column', gap:0, margin:0 }}>

      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div style={{
        display:'flex', alignItems:'center', gap:12, padding:'8px 16px',
        background:'#FFFFFF', borderBottom:'1px solid #E0E0E0', flexShrink:0, flexWrap:'wrap'
      }}>
        <button
          onClick={() => navigate(`/clients/${plan.client_id}`)}
          style={S.btnGhost}
        >← {plan.client_name}</button>

        <div style={{ fontWeight:700, fontSize:14, color:'#0A0A0A' }}>{plan.name}</div>

        <span style={{
          padding:'2px 9px', borderRadius:10, fontSize:11, fontWeight:700,
          background: STATUS_COLOR[plan.status]+'22', color: STATUS_COLOR[plan.status]
        }}>
          {STATUS_ICON[plan.status]} {plan.status}
        </span>

        <span style={{ fontSize:11, color:'#A09080' }}>v{plan.version}</span>

        {/* Save state indicator */}
        {saveState === 'saving' && <span style={{ fontSize:11, color:'#C19A6B' }}>⏳ Saving…</span>}
        {saveState === 'saved'  && <span style={{ fontSize:11, color:'#2A7A5A' }}>✓ Saved</span>}
        {saveState === 'error'  && <span style={{ fontSize:11, color:'#A02020' }}>✗ Save failed</span>}

        <div style={{ flex:1 }}/>

        {/* Status actions */}
        {plan.status === 'draft' && canEdit && (
          <button style={{ ...S.btnAction, color:'#B8860B', borderColor:'#F0883E44' }}
            onClick={() => handleStatusAction('Submit for Review')}>
            Submit for Review
          </button>
        )}
        {plan.status === 'review' && canApprove && (<>
          <button style={{ ...S.btnAction, color:'#2A7A5A', borderColor:'#3FB95044' }}
            onClick={() => handleStatusAction('Approve')}>✓ Approve</button>
          <button style={{ ...S.btnAction, color:'#A02020', borderColor:'#F8514944' }}
            onClick={() => handleStatusAction('Reject to Draft')}>✗ Reject</button>
        </>)}
        {isAdmin && plan.status !== 'archived' && (
          <button style={S.btnGhost} onClick={() => handleStatusAction('Archive')}>Archive</button>
        )}

        <button
          style={{ ...S.btnGhost, color: showPanel ? '#C19A6B' : '#9A8A7A' }}
          onClick={() => { setShowPanel(p => !p); if (!versions.length) loadVersions(); }}
        >
          💬 {plan.comments?.length || 0}
        </button>
      </div>

      {/* ── Main area ────────────────────────────────────────────────────── */}
      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>

        {/* Planning Engine — no overflow here; engine manages its own scroll internally */}
        <div style={{ flex:1, overflow:'hidden', position:'relative' }}>
          <PlanningEngine
            key={id}
            initialData={plan.data}
            onSave={!isReadOnly ? handleSave : null}
            planMeta={{ name: plan.name, client: plan.client_name }}
            readOnly={isReadOnly}
          />
        </div>

        {/* ── Right sidebar: comments + versions ── */}
        {showPanel && (
          <div style={{
            width:300, borderLeft:'1px solid #E0E0E0', background:'#FFFFFF',
            display:'flex', flexDirection:'column', flexShrink:0, overflow:'hidden'
          }}>
            <div style={{ padding:'12px 14px', borderBottom:'1px solid #E0E0E0', fontWeight:700, fontSize:13, color:'#0A0A0A' }}>
              Comments & History
            </div>

            {/* Comments */}
            <div style={{ flex:1, overflow:'auto', padding:12 }}>
              {plan.comments?.length === 0 && (
                <div style={{ color:'#A09080', fontSize:12, textAlign:'center', padding:16 }}>No comments yet</div>
              )}
              {plan.comments?.map(c => (
                <div key={c.id} style={{
                  background:'#F5F5F5', border:'1px solid #E0E0E0', borderRadius:8,
                  padding:'10px 12px', marginBottom:8,
                  opacity: c.resolved ? 0.5 : 1
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <span style={{ fontSize:11, fontWeight:600, color:'#C19A6B' }}>{c.user_name}</span>
                    <span style={{ fontSize:10, color:'#A09080' }}>
                      {new Date(c.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:'#2A2A2A', lineHeight:1.5 }}>{c.text}</div>
                  {c.resolved && <div style={{ fontSize:10, color:'#2A7A5A', marginTop:4 }}>✓ Resolved</div>}
                  {!c.resolved && canApprove && (
                    <button style={{ ...S.btnGhost, fontSize:10, padding:'3px 8px', marginTop:6 }}
                      onClick={() => api.resolveComment(id, c.id).then(load)}>
                      Mark resolved
                    </button>
                  )}
                </div>
              ))}

              {/* Versions */}
              {versions.length > 0 && (
                <div style={{ marginTop:16 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#9A8A7A', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>
                    Version History
                  </div>
                  {versions.slice(0,10).map(v => (
                    <div key={v.id} style={{ fontSize:11, color:'#A09080', marginBottom:6, padding:'6px 0', borderBottom:'1px solid #E0E0E0' }}>
                      <span style={{ color:'#C19A6B', fontWeight:600 }}>v{v.version}</span>
                      {' · '}{v.changed_by_name}
                      {' · '}{new Date(v.changed_at).toLocaleDateString()}
                      {v.note && <div style={{ color:'#9A8A7A', marginTop:2 }}>{v.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Comment input */}
            <div style={{ padding:12, borderTop:'1px solid #21262D' }}>
              <textarea
                value={comment} onChange={e => setComment(e.target.value)}
                placeholder="Add a comment…"
                rows={3}
                style={{
                  width:'100%', boxSizing:'border-box', resize:'none',
                  background:'#F5F5F5', border:'1px solid #30363D', borderRadius:6,
                  padding:'8px 10px', color:'#0A0A0A', fontSize:12,
                  fontFamily:'inherit', outline:'none', marginBottom:8
                }}
              />
              <button style={S.btnPrimary} onClick={handleComment} disabled={!comment.trim()}>
                Add Comment
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  btnGhost:  { background:'transparent', border:'1px solid #30363D', borderRadius:6, padding:'5px 12px', color:'#9A8A7A', fontSize:12, cursor:'pointer' },
  btnAction: { background:'transparent', border:'1px solid #30363D', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer', fontWeight:600 },
  btnPrimary:{ background:'#C19A6B', color:'#F5F5F5', border:'none', borderRadius:6, padding:'7px 14px', fontWeight:700, fontSize:12, cursor:'pointer', width:'100%' },
};
